/**
 * Typing turned into search calls: debounced (~200ms), one in flight at a
 * time, a stale answer discarded rather than drawn.
 *
 * Plain TS, no React -- `SearchField.tsx` is the only caller, wrapping one of
 * these in a `useRef` so a re-render never makes a second one.
 *
 * **What "abort" means here.** Neither `search()` (`@wtfalch/email/mailbox`)
 * nor `searchEvents` (`@wtfalch/calendar/calendar`) takes an `AbortSignal` --
 * only `MailClient.upload` does. So this cannot cancel a request already on
 * the wire; what it does is exactly what every other effect in this app does
 * with a `live` flag (`Mail.tsx`, `Today.tsx`, `Calendar.tsx`): a query
 * superseded by a newer one has its `AbortController` aborted, and its
 * answer, whenever it arrives, is checked against `signal.aborted` and
 * dropped rather than handed to `onState`.
 */
export type SearchState<Group> = {
  query: string;
  groups: readonly Group[];
  loading: boolean;
};

export type SearchRunner<Group> = (query: string, signal: AbortSignal) => Promise<readonly Group[]>;

export type SearchController = {
  /** Called on every keystroke. Debounces internally; the caller need not. */
  setQuery(query: string): void;
  /** Stops the debounce timer and aborts anything in flight -- call on
   *  unmount, the same as a `useEffect`'s own cleanup. */
  dispose(): void;
};

const DEBOUNCE_MS = 200;

export function createSearchController<Group>(
  run: SearchRunner<Group>,
  onState: (state: SearchState<Group>) => void,
  debounceMs = DEBOUNCE_MS,
): SearchController {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let controller: AbortController | null = null;

  function stopInFlight() {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
    controller?.abort();
    controller = null;
  }

  function setQuery(query: string) {
    stopInFlight();

    if (!query.trim()) {
      onState({ query, groups: [], loading: false });
      return;
    }

    onState({ query, groups: [], loading: true });
    timer = setTimeout(() => {
      timer = undefined;
      const ac = new AbortController();
      controller = ac;
      run(query, ac.signal).then(
        (groups) => {
          if (ac.signal.aborted) return;
          onState({ query, groups, loading: false });
        },
        () => {
          // `run` (`model.ts`'s `runSearch`) already turns every source's own
          // failure into its own group; a rejection here would only be a bug
          // in the runner itself. Treated the same as "nothing to show" so a
          // defect in one search never leaves the field stuck spinning.
          if (ac.signal.aborted) return;
          onState({ query, groups: [], loading: false });
        },
      );
    }, debounceMs);
  }

  return { setQuery, dispose: stopInFlight };
}
